import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPackage } from "../lib/packages";
import { getAllCustomers } from "../lib/users";
import { WAREHOUSE_LIST, UNITS } from "../lib/warehouses";
import { sendPackageReceivedEmail } from "../lib/emailNotifications";
import { detectCarrier, getTrackingURL } from "../lib/carrier";
import { Modal, PhotoUpload, toast } from "./UI";

const INIT = {
  customerEmail:  "",
  customerName:   "",
  trackingNumber: "",
  carrier:        "",
  weight:         "",
  dimensions:     "",
  eta:            "",
  status:         "Received",
  description:    "",
  notes:          "",
  warehouse:      "USA",
  unit:           "7000"
};

export default function AddPackageModal({ onClose, onSaved }) {
  const [form,        setForm]        = useState(INIT);
  const [photoFile,   setPhoto]       = useState(null);
  const [preview,     setPreview]     = useState(null);
  const [error,       setError]       = useState("");
  const [saving,      setSaving]      = useState(false);
  const [carrier,     setCarrier]     = useState(null);
  const [scanFlash,   setScanFlash]   = useState(false);
  const [scanMode,    setScanMode]    = useState(true);
  const [customers,   setCustomers]   = useState([]);
  const [custSuggest, setCustSuggest] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [lastKeyTime, setLastKeyTime] = useState(0);

  const scanRef   = useRef(null);
  const emailRef  = useRef(null);
  const bufferRef = useRef("");
  const timerRef  = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    getAllCustomers().then(setCustomers).catch(() => {});
    setTimeout(() => scanRef.current?.focus(), 100);
  }, []);

  const processScan = useCallback((tracking) => {
    const detected = detectCarrier(tracking);
    setCarrier(detected);
    setForm(f => ({
      ...f,
      trackingNumber: tracking,
      carrier: detected ? detected.name : f.carrier,
    }));
    try {
      const ctx  = new (window.AudioContext || window.webkitAudioContext)();
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.4, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      osc.start(); osc.stop(ctx.currentTime + 0.18);
    } catch (_) {}
    setScanFlash(true);
    setTimeout(() => setScanFlash(false), 700);
    setTimeout(() => emailRef.current?.focus(), 100);
    toast("✓ Scanned: " + tracking.slice(0, 20) + (tracking.length > 20 ? "..." : ""));
  }, []);

  useEffect(() => {
    if (!scanMode) return;
    const handleKey = (e) => {
      const now = Date.now();
      if (now - lastKeyTime > 300 && bufferRef.current.length > 0) {
        bufferRef.current = "";
      }
      setLastKeyTime(now);
      if (e.key === "Enter") {
        const scanned = bufferRef.current.trim();
        if (scanned.length > 6) { processScan(scanned); bufferRef.current = ""; }
        return;
      }
      if (e.key.length === 1) {
        bufferRef.current += e.key;
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          const scanned = bufferRef.current.trim();
          if (scanned.length > 10) { processScan(scanned); bufferRef.current = ""; }
        }, 500);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => { window.removeEventListener("keydown", handleKey); clearTimeout(timerRef.current); };
  }, [scanMode, lastKeyTime, processScan]);

  const handleTrackingChange = (val) => {
    set("trackingNumber", val);
    if (val.length > 6) {
      const detected = detectCarrier(val);
      setCarrier(detected);
      if (detected) set("carrier", detected.name);
    } else { setCarrier(null); }
  };

  const handleEmailChange = (val) => {
    set("customerEmail", val);
    if (val.length > 1) {
      const matches = customers.filter(c =>
        c.email.toLowerCase().includes(val.toLowerCase()) ||
        c.name.toLowerCase().includes(val.toLowerCase())
      ).slice(0, 6);
      setCustSuggest(matches);
      setShowSuggest(matches.length > 0);
    } else { setShowSuggest(false); }
  };

  const selectCustomer = (c) => {
    set("customerEmail", c.email);
    set("customerName", c.name);
    setShowSuggest(false);
    toast("✓ Customer: " + c.name);
  };

  const handlePhoto = (file) => {
    setPhoto(file);
    if (file) { const r = new FileReader(); r.onload = e => setPreview(e.target.result); r.readAsDataURL(file); }
    else setPreview(null);
  };

  const save = async () => {
    if (!form.customerEmail || !form.customerName || !form.weight) {
      setError("Customer email, name and weight are required."); return;
    }
    setSaving(true); setError("");
    try {
      const pkgId = await createPackage({ ...form }, photoFile);
      await sendPackageReceivedEmail({
        customerEmail:  form.customerEmail,
        customerName:   form.customerName,
        packageId:      pkgId,
        trackingNumber: form.trackingNumber || "—",
        description:    form.description,
        weight:         form.weight,
        warehouse:      form.warehouse || "USA",
        unit:           form.unit || "7000",
      });
      toast("Package added! Customer notified by email.");
      onSaved(); onClose();
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  const trackingURL = carrier ? getTrackingURL(form.trackingNumber, carrier.code) : null;

  return (
    <Modal title="Add new package" onClose={onClose}>
      {error && <div className="error-msg">{error}</div>}

      {/* Scan mode banner */}
      <div
        onClick={() => setScanMode(m => !m)}
        style={{
          display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
          padding: "10px 14px", borderRadius: "var(--radius)", cursor: "pointer",
          background: scanMode ? "var(--green-light)" : "var(--bg-soft)",
          border: `1.5px solid ${scanMode ? "var(--green-mid)" : "var(--border)"}`,
          transition: "all .2s",
        }}
      >
        <span style={{ fontSize: 22 }}>▦</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: scanMode ? "var(--green-dark)" : "var(--text)" }}>
            {scanMode ? "🟢 Scan mode ON — Ready to scan" : "⚪ Scan mode OFF"}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            {scanMode
              ? "Point your USB scanner at any barcode — works anywhere on the page"
              : "Click to enable scan mode"}
          </div>
        </div>
        <div style={{
          fontSize: 11, padding: "4px 12px", borderRadius: 20, fontWeight: 700,
          background: scanMode ? "var(--green)" : "var(--bg-tertiary)",
          color: scanMode ? "#fff" : "var(--text-muted)",
        }}>
          {scanMode ? "ON" : "OFF"}
        </div>
      </div>

      {/* Tracking number */}
      <div className="form-row">
        <label>
          Tracking number
          {scanMode && <span style={{ marginLeft: 6, fontSize: 11, color: "var(--green-dark)", background: "var(--green-light)", padding: "1px 8px", borderRadius: 10, fontWeight: 600 }}>auto-scan active</span>}
        </label>
        <div className="scan-input-wrap">
          <span className="scan-icon">▦</span>
          <input
            ref={scanRef}
            className={"scan-input" + (scanFlash ? " scan-flash" : "")}
            placeholder="Scan barcode or type tracking number..."
            value={form.trackingNumber}
            onChange={e => handleTrackingChange(e.target.value)}
            autoComplete="off"
          />
          {form.trackingNumber && (
            <button className="scan-clear" onClick={() => { set("trackingNumber", ""); set("carrier", ""); setCarrier(null); }}>×</button>
          )}
        </div>
        {carrier && (
          <div className="carrier-badge" style={{ background: carrier.bg, color: carrier.color }}>
            <span className="carrier-dot" style={{ background: carrier.color }} />
            <strong>{carrier.name}</strong> detected automatically
            {trackingURL && (
              <a href={trackingURL} target="_blank" rel="noreferrer" className="carrier-track-link" style={{ color: carrier.color }}>
                Track on {carrier.name} ↗
              </a>
            )}
          </div>
        )}
      </div>

      <div className="form-divider" />

      {/* Customer email with dropdown autocomplete */}
      <div className="form-row" style={{ position: "relative" }}>
        <label>Customer email *</label>
        <input
          ref={emailRef}
          id="pkg-customer-email"
          type="email"
          value={form.customerEmail}
          onChange={e => handleEmailChange(e.target.value)}
          onFocus={e => handleEmailChange(e.target.value)}
          onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
          placeholder="Type email or name to search customers..."
          autoComplete="off"
        />
        {showSuggest && (
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 1000,
            background: "var(--bg)", border: "1px solid var(--border-md)",
            borderRadius: "var(--radius)", boxShadow: "var(--shadow-md)", overflow: "hidden",
          }}>
            {custSuggest.map(c => (
              <div
                key={c.uid}
                onMouseDown={() => selectCustomer(c)}
                style={{
                  padding: "10px 14px", cursor: "pointer", display: "flex",
                  alignItems: "center", gap: 10, borderBottom: "0.5px solid var(--border)",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--bg-soft)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <div style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: "var(--green-light)", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  fontSize: 14, fontWeight: 700, color: "var(--green-dark)", flexShrink: 0,
                }}>
                  {c.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{c.email}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="form-row">
        <label>Customer full name *</label>
        <input
          value={form.customerName}
          onChange={e => set("customerName", e.target.value)}
          placeholder="Auto-filled when you select a customer above"
        />
      </div>

      <div className="grid2">
        <div className="form-row">
          <label>Weight (lbs) *</label>
          <input type="number" step="0.1" value={form.weight} onChange={e => set("weight", e.target.value)} placeholder="2.5" />
        </div>
        <div className="form-row">
          <label>Dimensions (in)</label>
          <input value={form.dimensions} onChange={e => set("dimensions", e.target.value)} placeholder="12x8x6" />
        </div>
      </div>

      <div className="form-row">
        <label>Estimated delivery date</label>
        <input type="date" value={form.eta} onChange={e => set("eta", e.target.value)} />
      </div>

      <div className="form-row">
        <label>Status</label>
        <select value={form.status} onChange={e => set("status", e.target.value)}>
          <option value="Received">Received</option>
          <option value="Shipped">In Transit</option>
          <option value="Delivered">Delivered</option>
        </select>
      </div>

      <div className="grid2">
        <div className="form-row">
          <label>Warehouse</label>
          <select value={form.warehouse} onChange={e => set("warehouse", e.target.value)}>
            {WAREHOUSE_LIST.map(w => <option key={w.id} value={w.id}>{w.flag} {w.label}</option>)}
          </select>
        </div>
        <div className="form-row">
          <label>Unit</label>
          <select value={form.unit} onChange={e => set("unit", e.target.value)}>
            {Object.entries(UNITS).map(([k, v]) => <option key={k} value={k}>{v.label} — {v.desc}</option>)}
          </select>
        </div>
      </div>

      <div className="form-row">
        <label>Description</label>
        <input value={form.description} onChange={e => set("description", e.target.value)} placeholder="Electronics, clothing..." />
      </div>

      <div className="form-row">
        <label>Warehouse notes</label>
        <textarea rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Notes visible to customer..." />
      </div>

      <div className="form-row">
        <label>Package photo</label>
        <PhotoUpload onChange={handlePhoto} preview={preview} />
      </div>

      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save package"}
        </button>
      </div>
    </Modal>
  );
}
